import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import {
  ACTIVITY_STATUS,
  ACTIVITY_STATUS_ORDER,
  INSCRIPTION_STATUS,
  INSCRIPTION_STATUS_ORDER,
} from "@/lib/statuses";
import {
  Loader2,
  LogOut,
  Users,
  ListChecks,
  Mail,
  Phone,
  ChevronDown,
  MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_SRC = "/brand/pazapa-logo.jpg";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inscriptions");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!ready || !user) return;
    axios.get(`${API}/admin/summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, [ready, user]);

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="" className="w-11 h-11 rounded-full object-cover" />
            <div>
              <div className="font-heading text-xl text-primary leading-none">PAZAPA · Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60 mt-1">
                {user.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm font-semibold text-foreground/70 hover:text-primary hidden md:inline">
              Voir le site public
            </a>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="pill-btn bg-white border-2 border-foreground text-foreground hover:bg-foreground hover:text-background"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
              Se déconnecter
            </button>
          </div>
        </div>
      </header>

      {/* Summary cards */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          <StatCard label="Inscriptions" value={summary?.inscriptions ?? "—"} accent="hsl(var(--primary))" />
          <StatCard label="Nouvelles inscriptions" value={summary?.inscriptions_new ?? "—"} accent="hsl(var(--accent))" />
          <StatCard label="Demandes contact" value={summary?.talk_requests ?? "—"} accent="hsl(var(--coral))" />
          <StatCard label="Activités" value={summary?.activities ?? "—"} accent="hsl(var(--plum))" />
          <StatCard label="Newsletter" value={summary?.newsletter ?? "—"} accent="hsl(var(--secondary))" />
        </div>

        {summary?.notify_email && (
          <div
            className="mt-4 rounded-2xl border-2 border-dashed p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            style={{ borderColor: "hsl(var(--accent))" }}
            data-testid="notify-email-card"
          >
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/60">
                Notifications e-mail envoyées à
              </div>
              <div className="mt-1 font-heading text-lg break-all">{summary.notify_email}</div>
              {summary.notify_email === "delivered@resend.dev" && (
                <div className="mt-1 text-xs text-foreground/60">
                  Adresse de test (visible dans le tableau de bord Resend). Communiquez-nous votre vraie adresse pour la basculer.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-border">
          <TabBtn active={tab === "inscriptions"} onClick={() => setTab("inscriptions")} testId="tab-inscriptions">
            <ListChecks className="w-4 h-4" /> Inscriptions
          </TabBtn>
          <TabBtn active={tab === "talks"} onClick={() => setTab("talks")} testId="tab-talks">
            <MessageCircle className="w-4 h-4" /> Demandes contact
          </TabBtn>
          <TabBtn active={tab === "activities"} onClick={() => setTab("activities")} testId="tab-activities">
            <Users className="w-4 h-4" /> Activités
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === "inscriptions" && <InscriptionsPanel />}
          {tab === "talks" && <TalksPanel />}
          {tab === "activities" && <ActivitiesPanel />}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5">
      <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">{label}</div>
      <div className="mt-2 font-heading text-3xl" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-3 font-semibold text-sm border-b-2 -mb-px ${
        active ? "border-primary text-primary" : "border-transparent text-foreground/70 hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================
// Inscriptions panel
// ============================================================
function InscriptionsPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/inscriptions`);
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/admin/inscriptions/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Impossible de mettre à jour.");
    } finally {
      setBusyId(null);
    }
  };

  if (!items) {
    return (
      <div className="py-16 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="inscriptions-panel">
      <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label="Filtrer par statut">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="ins-filter-all">
          Toutes ({items.length})
        </FilterChip>
        {INSCRIPTION_STATUS_ORDER.map((s) => {
          const count = items.filter((i) => i.status === s).length;
          const meta = INSCRIPTION_STATUS[s];
          return (
            <FilterChip
              key={s}
              active={filter === s}
              onClick={() => setFilter(s)}
              testId={`ins-filter-${s}`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: meta.bg }}
              />
              {meta.label} ({count})
            </FilterChip>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-border p-12 text-center">
          <div className="font-heading text-xl">Aucune inscription à afficher.</div>
          <p className="mt-2 text-sm text-foreground/60">
            Les nouvelles inscriptions apparaîtront ici automatiquement.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ins) => (
            <InscriptionRow
              key={ins.id}
              inscription={ins}
              busy={busyId === ins.id}
              onChangeStatus={(s) => changeStatus(ins.id, s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-2 rounded-full text-sm font-semibold border-2 ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-white text-foreground/80 border-border hover:border-foreground/25"
      }`}
    >
      {children}
    </button>
  );
}

function EmailBadge({ inscription }) {
  const admin = inscription.email_admin_status;
  const parent = inscription.email_parent_status;
  if (!admin && !parent) return null;
  const dot = (s) => {
    if (s === "sent") return "bg-green-500";
    if (s === "failed") return "bg-red-500";
    return "bg-foreground/25";
  };
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-foreground/60"
      title={`Admin: ${admin || "—"} · Parent: ${parent || "—"}`}
      data-testid={`email-badge-${inscription.id}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot(admin)}`} />
      <span className={`w-1.5 h-1.5 rounded-full ${dot(parent)}`} />
      email
    </span>
  );
}

function InscriptionRow({ inscription, busy, onChangeStatus }) {
  const [open, setOpen] = useState(false);
  const meta = INSCRIPTION_STATUS[inscription.status] || INSCRIPTION_STATUS.nouveau;
  const date = new Date(inscription.created_at);

  return (
    <div
      className="rounded-2xl bg-white border border-border overflow-hidden"
      data-testid={`inscription-row-${inscription.id}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-3 md:gap-6"
      >
        <div
          className="px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </div>
        <div className="min-w-[180px] flex-1">
          <div className="font-heading text-base">
            {inscription.parent_first_name} {inscription.parent_last_name}
          </div>
          <div className="text-xs text-foreground/60 mt-0.5">
            {inscription.email} · {inscription.child_age_bracket}
          </div>
        </div>
        <div className="hidden md:block text-sm text-foreground/70">
          {inscription.activity_title || inscription.activity_slug}
        </div>
        <EmailBadge inscription={inscription} />
        <div className="hidden md:block text-xs text-foreground/50 ml-auto">
          {date.toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-border">
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div className="space-y-2 text-sm">
              <Field label="Activité">{inscription.activity_title || inscription.activity_slug}</Field>
              <Field label="Enfant">
                {inscription.child_first_name || "—"} · {inscription.child_age_bracket}
              </Field>
              <Field label="Langue">{inscription.preferred_lang?.toUpperCase()}</Field>
              <Field label="Disponibilités">{inscription.availability || "—"}</Field>
              <Field label="Note du parent">
                <span className="italic text-foreground/80">
                  {inscription.notes || "—"}
                </span>
              </Field>
            </div>
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                  Contact
                </div>
                <div className="mt-2 flex flex-col gap-2">
                  <a href={`mailto:${inscription.email}`} className="inline-flex items-center gap-2 text-primary font-semibold">
                    <Mail className="w-4 h-4" /> {inscription.email}
                  </a>
                  {inscription.phone && (
                    <a href={`tel:${inscription.phone}`} className="inline-flex items-center gap-2 text-primary font-semibold">
                      <Phone className="w-4 h-4" /> {inscription.phone}
                    </a>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                  Changer le statut
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INSCRIPTION_STATUS_ORDER.map((s) => {
                    const m = INSCRIPTION_STATUS[s];
                    const active = inscription.status === s;
                    return (
                      <button
                        key={s}
                        onClick={() => !active && onChangeStatus(s)}
                        disabled={busy}
                        data-testid={`ins-status-${inscription.id}-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 disabled:opacity-50 ${
                          active
                            ? "border-transparent"
                            : "border-border bg-white hover:border-foreground/25 text-foreground"
                        }`}
                        style={active ? { backgroundColor: m.bg, color: m.fg } : {}}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============================================================
// Activities panel
// ============================================================
function ActivitiesPanel() {
  const { t, localized } = useLang();
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/activities`);
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/admin/activities/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Impossible de mettre à jour.");
    } finally {
      setBusyId(null);
    }
  };

  if (!items) {
    return (
      <div className="py-16 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="activities-panel">
      {items.map((a) => {
        const meta = ACTIVITY_STATUS[a.status] || ACTIVITY_STATUS.bientot;
        return (
          <div
            key={a.id}
            className="rounded-2xl bg-white border border-border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
            data-testid={`admin-activity-${a.slug}`}
          >
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {a.image_url && (
                <img
                  src={a.image_url}
                  alt=""
                  className="w-16 h-16 rounded-2xl object-cover shrink-0"
                />
              )}
              <div className="min-w-0">
                <div className="font-heading text-lg leading-tight">{localized(a, "title")}</div>
                <div className="text-sm text-foreground/70 line-clamp-1">{localized(a, "benefit")}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground/60">
                    {a.age_range}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-muted text-foreground/60">
                    {a.language}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 md:min-w-[280px]">
              <div className="flex items-center gap-2">
                <div
                  className="px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: meta.bg, color: meta.fg }}
                >
                  {t(meta.labelKey)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ACTIVITY_STATUS_ORDER.map((s) => {
                  const m = ACTIVITY_STATUS[s];
                  const active = a.status === s;
                  return (
                    <button
                      key={s}
                      onClick={() => !active && changeStatus(a.id, s)}
                      disabled={busyId === a.id}
                      data-testid={`activity-status-${a.slug}-${s}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 disabled:opacity-50 ${
                        active
                          ? "border-transparent"
                          : "border-border bg-white hover:border-foreground/30 text-foreground"
                      }`}
                      style={active ? { backgroundColor: m.bg, color: m.fg } : {}}
                    >
                      {t(m.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ============================================================
// Talk requests panel (Parler à l'équipe)
// ============================================================
const NEED_LABELS = {
  ecole: "Avancer à l'école",
  bouger: "Bouger et motricité",
  lien: "Confiance et lien",
  creer: "Découvrir et s'épanouir",
  orientation: "Ne sait pas encore",
};

function TalksPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/talk-requests`);
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, s) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/admin/talk-requests/${id}`, { status: s });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Impossible de mettre à jour.");
    } finally {
      setBusyId(null);
    }
  };

  if (!items) {
    return (
      <div className="py-16 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="talks-panel">
      <div className="flex flex-wrap gap-2 mb-5">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="talk-filter-all">
          Toutes ({items.length})
        </FilterChip>
        {INSCRIPTION_STATUS_ORDER.map((s) => {
          const count = items.filter((i) => i.status === s).length;
          const meta = INSCRIPTION_STATUS[s];
          return (
            <FilterChip
              key={s}
              active={filter === s}
              onClick={() => setFilter(s)}
              testId={`talk-filter-${s}`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: meta.bg }}
              />
              {meta.label} ({count})
            </FilterChip>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-border p-12 text-center">
          <div className="font-heading text-xl">Aucune demande à afficher.</div>
          <p className="mt-2 text-sm text-foreground/60">
            Les nouveaux messages « Parler à l'équipe » apparaissent ici automatiquement.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((req) => (
            <TalkRow
              key={req.id}
              req={req}
              busy={busyId === req.id}
              onChangeStatus={(s) => changeStatus(req.id, s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TalkRow({ req, busy, onChangeStatus }) {
  const [open, setOpen] = useState(false);
  const meta = INSCRIPTION_STATUS[req.status] || INSCRIPTION_STATUS.nouveau;
  const date = new Date(req.created_at);

  return (
    <div
      className="rounded-2xl bg-white border border-border overflow-hidden"
      data-testid={`talk-row-${req.id}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-3 md:gap-6"
      >
        <div
          className="px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </div>
        <div className="min-w-[180px] flex-1">
          <div className="font-heading text-base">
            {req.parent_first_name} {req.parent_last_name}
          </div>
          <div className="text-xs text-foreground/60 mt-0.5">
            {req.email} · {req.child_age_bracket}
          </div>
        </div>
        <div className="hidden md:block text-sm text-foreground/70">
          {NEED_LABELS[req.main_need] || req.main_need}
        </div>
        <EmailBadge inscription={req} />
        <div className="hidden md:block text-xs text-foreground/50 ml-auto">
          {date.toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-border">
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div className="space-y-2 text-sm">
              <Field label="Besoin principal">{NEED_LABELS[req.main_need] || req.main_need}</Field>
              <Field label="Enfant">{req.child_age_bracket}</Field>
              <Field label="Commune">{req.commune || "—"}</Field>
              <Field label="Langue">{req.preferred_lang?.toUpperCase()}</Field>
              <Field label="Préférence contact">{req.contact_preference || "—"}</Field>
              <Field label="Disponibilités">{req.availability || "—"}</Field>
            </div>
            <div className="space-y-3">
              <div className="text-sm">
                <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                  Contact
                </div>
                <div className="mt-2 flex flex-col gap-2">
                  <a href={`mailto:${req.email}`} className="inline-flex items-center gap-2 text-primary font-semibold">
                    <Mail className="w-4 h-4" /> {req.email}
                  </a>
                  {req.phone && (
                    <a href={`tel:${req.phone}`} className="inline-flex items-center gap-2 text-primary font-semibold">
                      <Phone className="w-4 h-4" /> {req.phone}
                    </a>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-foreground/60">
                  Changer le statut
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INSCRIPTION_STATUS_ORDER.map((s) => {
                    const m = INSCRIPTION_STATUS[s];
                    const active = req.status === s;
                    return (
                      <button
                        key={s}
                        onClick={() => !active && onChangeStatus(s)}
                        disabled={busy}
                        data-testid={`talk-status-${req.id}-${s}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 disabled:opacity-50 ${
                          active
                            ? "border-transparent"
                            : "border-border bg-white hover:border-foreground/25 text-foreground"
                        }`}
                        style={active ? { backgroundColor: m.bg, color: m.fg } : {}}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
